import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { DEFAULT_MIN_ODDS } from "../../config/oddsPolicy";
import {
  LEDGER_COLUMNS,
  LedgerTable,
  Pager,
  SortHeader,
  StatusBadge,
  downloadCsv,
  humanize,
  useDebounced,
  useSort,
} from "./ResearchParts";
import "./football-intelligence.css";
import "./fi-extra.css";

const PAGE = 50;
const DEFAULT_FILTERS = { min_odds: String(DEFAULT_MIN_ODDS), max_odds: "", freshness: "all" };
const READINESS_ORDER = { READY: 0, NEEDS_HISTORY: 1, INSUFFICIENT_DATA: 2, NO_PROVIDER_MAPPING: 3 };

// Empty strings must never reach the API: FastAPI rejects `fixture_id=` and `min_ev=`.
const cleanParams = (filters) => Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined && v !== null));

const dayLabel = (coverage) => {
  if (!coverage?.date) return "Fixture-day";
  const date = new Date(`${coverage.date}T12:00:00Z`);
  return `${date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}`;
};

const sum = (rows, key) => rows.reduce((n, r) => n + (Number(r[key]) || 0), 0);

const COVERAGE_SORT = {
  competition: (r) => r.competition,
  fixtures: (r) => r.fixtures,
  odds: (r) => r.fixtures_with_odds,
  history: (r) => r.historical_fixtures,
  readiness: (r) => READINESS_ORDER[r.fit_readiness] ?? 9,
};

/**
 * Fixture-day research: which competitions are fit-ready, plus the frozen,
 * quote-linked hypothetical forecasts. Everything here is private research;
 * nothing is a published pick and abstentions are never shown as zero.
 */
export default function SaturdayResearch() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const filters = useDebounced(draft, 300);
  const [coverageQuery, setCoverageQuery] = useState("");
  const [coverageMode, setCoverageMode] = useState("all");

  const change = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setOffset(0);
  };

  useEffect(() => {
    let alive = true;
    api
      .get("/api/football/intelligence/research", { params: { mode: "saturday", limit: PAGE, offset, ...cleanParams(filters) } })
      .then((response) => {
        if (alive) {
          setData(response.data);
          setError("");
        }
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.detail || "Fixture-day research unavailable");
      });
    return () => {
      alive = false;
    };
  }, [filters, offset]);

  const coverageRows = useMemo(() => data?.coverage?.rows || [], [data]);
  const fixtures = useMemo(() => data?.fixtures || [], [data]);
  const fixtureMap = useMemo(() => new Map(fixtures.map((f) => [Number(f.id), f])), [fixtures]);
  const fixtureName = (id) => {
    const f = fixtureMap.get(Number(id));
    return f ? `${f.home_team} v ${f.away_team}` : "";
  };

  const visibleCoverage = useMemo(() => {
    const needle = coverageQuery.trim().toLowerCase();
    return coverageRows.filter(
      (r) =>
        (!needle || String(r.competition).toLowerCase().includes(needle)) &&
        (coverageMode === "all" ||
          (coverageMode === "odds" && r.fixtures_with_odds > 0) ||
          (coverageMode === "ready" && r.fit_readiness === "READY"))
    );
  }, [coverageRows, coverageQuery, coverageMode]);
  const { sorted: coverageSorted, sort, toggle } = useSort(visibleCoverage, "readiness", COVERAGE_SORT);

  const compCodes = useMemo(() => [...new Set(fixtures.map((f) => f.comp).filter(Boolean))].sort(), [fixtures]);
  const markets = useMemo(() => [...new Set((data?.rows || []).map((r) => r.market))].sort(), [data]);
  const models = useMemo(() => [...new Set((data?.rows || []).map((r) => r.model_id))].sort(), [data]);
  const eligibilities = useMemo(() => [...new Set((data?.rows || []).map((r) => r.eligibility_status))].sort(), [data]);

  if (error) return <section className="fi-card"><p role="alert">{error}</p></section>;
  if (!data) return <section className="fi-card"><p role="status">Loading fixture-day evidence…</p></section>;

  const coverage = data.coverage || {};
  const statusCounts = Object.entries(coverage.status_counts || {});
  const abstentions = Object.entries(data.abstentions || {}).sort((a, b) => b[1] - a[1]);

  return (
    <section className="fi-card">
      <h3>{dayLabel(coverage)} · coverage & frozen comparisons</h3>
      <p>
        {coverage.timezone || "Local"} day. Fitted does not mean forecastable. Provider rights remain restricted; these are private hypothetical forecasts, never published picks.
      </p>

      <div className="fx-tiles">
        <div className="fx-tile"><span>Fixtures</span><strong>{coverage.fixtures ?? "—"}</strong><small>{coverage.competitions ?? "—"} competitions</small></div>
        <div className="fx-tile"><span>With stored odds</span><strong>{sum(coverageRows, "fixtures_with_odds")}</strong><small>fixtures</small></div>
        <div className="fx-tile"><span>With Model A</span><strong>{sum(coverageRows, "fixtures_with_model_a")}</strong><small>fixtures</small></div>
        <div className="fx-tile"><span>Positive persisted edges</span><strong>{sum(coverageRows, "positive_persisted_edge_fixtures")}</strong><small>fixtures · research only</small></div>
        <div className="fx-tile"><span>Ledger records</span><strong>{data.total}</strong><small>after filters</small></div>
      </div>

      <div className="fx-toolbar">
        <span className="fx-coverage">Competition readiness:</span>
        {statusCounts.map(([status, count]) => <span key={status}><StatusBadge value={status} /> <b>{count}</b></span>)}
      </div>
      {coverage.shortlist_note && <p className="fx-callout"><strong>Note.</strong> {coverage.shortlist_note}</p>}

      <h4 style={{ margin: "18px 0 8px", fontSize: 14 }}>Competition coverage</h4>
      <div className="fx-toolbar">
        <label className="fx-field">Search competitions
          <input type="search" value={coverageQuery} onChange={(e) => setCoverageQuery(e.target.value)} placeholder="e.g. Bundesliga" aria-label="Search competitions" />
        </label>
        <div className="fx-chips" role="group" aria-label="Coverage filter">
          {[["all", "All"], ["odds", "With stored odds"], ["ready", "Fit-ready"]].map(([key, label]) => (
            <button key={key} type="button" className="fx-chip" aria-pressed={coverageMode === key} onClick={() => setCoverageMode(key)}>{label}</button>
          ))}
        </div>
        <span className="fx-coverage"><b>{coverageSorted.length}</b> of <b>{coverageRows.length}</b> competitions</span>
      </div>
      <div className="fx-scroll">
        <table style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <SortHeader label="Competition" k="competition" sort={sort} toggle={toggle} />
              <SortHeader label="Fixtures" k="fixtures" sort={sort} toggle={toggle} numeric />
              <SortHeader label="With odds" k="odds" sort={sort} toggle={toggle} numeric />
              <th className="fx-num">Model A</th>
              <th className="fx-num">Positive</th>
              <SortHeader label="History" k="history" sort={sort} toggle={toggle} numeric />
              <th>Variants</th>
              <SortHeader label="Readiness" k="readiness" sort={sort} toggle={toggle} />
              <th>Blockers</th>
            </tr>
          </thead>
          <tbody>
            {coverageSorted.map((r) => (
              <tr key={r.csb_league_id}>
                <td>{r.competition}<small>id {r.csb_league_id}</small></td>
                <td className="fx-num">{r.fixtures}</td>
                <td className="fx-num">{r.fixtures_with_odds}</td>
                <td className="fx-num">{r.fixtures_with_model_a}</td>
                <td className="fx-num">{r.positive_persisted_edge_fixtures}</td>
                <td className="fx-num">{r.historical_fixtures}</td>
                <td>
                  {r.goals_variants?.length ? <>Goals: {r.goals_variants.join(", ")}</> : <small>No goals variants</small>}
                  {r.corners_variants?.length ? <small>Corners: {r.corners_variants.join(", ")}</small> : null}
                </td>
                <td><StatusBadge value={r.fit_readiness} /><small>{humanize(r.usage_rights)}</small></td>
                <td>{(r.blockers || []).map((b) => <div key={b} style={{ marginBottom: 3 }}><StatusBadge value={b} /></div>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 style={{ margin: "26px 0 8px", fontSize: 14 }}>Comparable market evidence</h4>
      <p className="fi-note">Only identical fixture, exact market/line and bookmaker quote support head-to-head comparisons. Missing forecasts are abstentions, not zero probability. Repeated bookmakers and markets are correlated.</p>

      <div className="fx-toolbar">
        <label className="fx-field">League code<input list="fi-comp-codes" aria-label="League code" value={draft.competition || ""} onChange={(e) => change("competition", e.target.value)} placeholder="e.g. SERIE_A" /></label>
        <label className="fx-field">Fixture<input list="fi-fixture-ids" aria-label="Fixture ID" inputMode="numeric" value={draft.fixture_id || ""} onChange={(e) => change("fixture_id", e.target.value.replace(/\D/g, ""))} placeholder="id or pick" /></label>
        <label className="fx-field">Market<input list="fi-markets" aria-label="Market" value={draft.market || ""} onChange={(e) => change("market", e.target.value)} placeholder="e.g. BTTS_Y" /></label>
        <label className="fx-field">Model<input list="fi-models" aria-label="Model A / B:variant" value={draft.model || ""} onChange={(e) => change("model", e.target.value)} placeholder="A or B:xg" /></label>
        <label className="fx-field">Min odds<input type="number" step="0.05" min="1.01" aria-label="Minimum odds" value={draft.min_odds ?? ""} onChange={(e) => change("min_odds", e.target.value)} style={{ width: 90 }} /></label>
        <label className="fx-field">Max odds<input type="number" step="0.05" min="1.01" aria-label="Maximum odds" value={draft.max_odds ?? ""} onChange={(e) => change("max_odds", e.target.value)} style={{ width: 90 }} /></label>
        <label className="fx-field">Min EV (0.05 = 5%)<input type="number" step="0.01" aria-label="Minimum EV (decimal)" value={draft.min_ev ?? ""} onChange={(e) => change("min_ev", e.target.value)} style={{ width: 110 }} /></label>
        <label className="fx-field">Eligibility<input list="fi-elig" aria-label="Eligibility status" value={draft.eligibility || ""} onChange={(e) => change("eligibility", e.target.value)} placeholder="e.g. OBSERVED_BASELINE" /></label>
        <label className="fx-field">Quote freshness
          <select aria-label="Quote freshness" value={draft.freshness} onChange={(e) => change("freshness", e.target.value)}>
            <option value="all">All, labelled</option><option value="fresh">≤6 hours</option><option value="stale">Stale / unknown</option>
          </select>
        </label>
        <button type="button" className="fx-button" onClick={() => { setDraft(DEFAULT_FILTERS); setOffset(0); }}>Reset filters</button>
        <button type="button" className="fx-button" disabled={!data.rows?.length} onClick={() => downloadCsv(`csb-research-ledger-${coverage.date || "day"}-page${offset / PAGE + 1}.csv`, data.rows, LEDGER_COLUMNS(fixtureName))}>Export this page (CSV)</button>
      </div>
      <datalist id="fi-comp-codes">{compCodes.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="fi-fixture-ids">{fixtures.slice(0, 400).map((f) => <option key={f.id} value={f.id}>{f.home_team} v {f.away_team} · {f.comp}</option>)}</datalist>
      <datalist id="fi-markets">{markets.map((m) => <option key={m} value={m} />)}</datalist>
      <datalist id="fi-models">{models.map((m) => <option key={m} value={m} />)}</datalist>
      <datalist id="fi-elig">{eligibilities.map((e) => <option key={e} value={e} />)}</datalist>

      <LedgerTable rows={data.rows} fixtureName={fixtureName} />
      <Pager offset={offset} limit={PAGE} total={data.total} hasMore={data.has_more} onChange={setOffset} />

      <h4 style={{ margin: "26px 0 8px", fontSize: 14 }}>Why forecasts are missing</h4>
      {abstentions.length ? (
        <div className="fx-scroll" style={{ maxHeight: "none" }}>
          <table>
            <thead><tr><th>Reason</th><th className="fx-num">Records</th></tr></thead>
            <tbody>{abstentions.map(([reason, count]) => <tr key={reason}><td>{humanize(reason)}</td><td className="fx-num">{count}</td></tr>)}</tbody>
          </table>
        </div>
      ) : <p className="fi-note">No abstention counts were supplied.</p>}

      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: "pointer" }}>Fixture list ({fixtures.length})</summary>
        <div className="fx-scroll" style={{ marginTop: 10 }}>
          <table style={{ minWidth: 560 }}>
            <thead><tr><th>Fixture</th><th>Kickoff</th><th>Competition</th></tr></thead>
            <tbody>
              {fixtures.map((f) => (
                <tr key={f.id}>
                  <td>{f.home_team} – {f.away_team}<small>#{f.id}</small></td>
                  <td>{f.kickoff_utc ? new Date(f.kickoff_utc).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                  <td>{f.comp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
